import { put, call, all, delay, takeEvery, takeLatest } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import { message } from 'antd'
import {
  list as rolesList,
  add as roleAdd,
  get as roleGet,
  update as roleUpdate,
  del as roleDelete,
  search as rolesSearch,
  listAllPermssions as rolesPermissionsList,
  listPermssionRestrictions as listRoleRestrictions,
  addPermssionRestrictions as addRoleRestriction,
  delPermssionRestrictions as delRoleRestriction,
  listRolePermissionsV2,
  getRolePermissionsV2Values,
  updateRolePermissionsV2,
  resetRolePermissionsV2,
} from 'services/roles'
import _ from 'lodash'
import { getAppHost, isAppModerator } from 'helpers'
import actions from './actions'

export function* ROLES_LIST({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: {
      areRolesLoading: true,
    },
  })

  const activeApp = payload.data || reduxStore.getState().apps.active
  const appSettings = payload.appSettings || reduxStore.getState().apps.appSettings
  // Trimmed like SEARCH_ROLE — this serves page 2+ of an active search.
  const searchText = reduxStore.getState().roles.roleSearchText.trim()
  const rolesPagination = reduxStore.getState().roles.rolesListPagination

  let pageNo = payload.currentPage
  if (pageNo === undefined && rolesPagination.current_page !== undefined)
    pageNo = rolesPagination.current_page + 1

  try {
    const success = yield call(
      rolesList,
      activeApp,
      getAppHost(appSettings),
      pageNo,
      searchText,
    )

    if (success && success.data) {
      let totalRoleCount = success.data.length
      let rolesListPagination = {}
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalRoleCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
        rolesListPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesList: success.data,
          rolesListTotal: totalRoleCount,
          rolesListPagination,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesList: [],
          rolesListTotal: 0,
          rolesListPagination: {},
          areRolesLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch roles', 'error')
  }
}

export function* GET_ROLE({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const { id } = payload
  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(roleGet, id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesActive: success.data,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      yield put(push(`/app/${activeApp.id}/roles`))
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch role', 'error')
  }
}

export function* ADD_ROLE({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  const filtered = {}
  _.filter(payload, function fn(n, k) {
    if (payload[k] && _.trim(payload[k]) !== '') {
      filtered[k] = n
    }
  })

  try {
    const success = yield call(roleAdd, filtered, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.ROLES_LIST,
        payload: { currentPage: 1, data: activeApp, appSettings },
      })
      yield put(push(`/app/${activeApp.id}/roles`))
      window.triggerNotification('Role Added', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to add role', 'error')
  }
}

export function* ENTER_ROLE_TEXT({ payload }) {
  const { searchText, clearText } = payload
  if (clearText) {
    yield put({
      type: actions.SET_STATE,
      payload: { roleSearchText: '' },
    })
  } else {
    yield put({
      type: actions.SET_STATE,
      payload: { roleSearchText: searchText },
    })
    // Debounce — the watcher below uses takeLatest so a newer keystroke cancels
    // this delay. Without both, every keystroke fires an uncancelled request and
    // a slower earlier response can overwrite a newer one. Mirrors
    // users/sagas.ts ENTER_USER_TEXT and groups/sagas.ts ENTER_GROUP_TEXT.
    yield delay(300)
    yield put({
      type: actions.SEARCH_ROLE,
      payload: {},
    })
  }
}

export function* SEARCH_ROLE() {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps
  // Trimmed at the API boundary: state keeps what the user typed (it is the
  // input's value), the endpoint gets a term it can prefix-match.
  const searchText = reduxStore.getState().roles.roleSearchText.trim()

  try {
    const success = yield call(rolesSearch, activeApp, getAppHost(appSettings), searchText)

    if (success && success.data) {
      let totalRoleCount = success.data.length
      const rolesListPagination = success.meta && success.meta.pagination ? success.meta.pagination : {}
      if (success.meta && success.meta.pagination && success.meta.pagination.total_pages) {
        totalRoleCount = success.meta.pagination.total_pages * success.meta.pagination.per_page
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesList: success.data,
          rolesListTotal: totalRoleCount,
          rolesListPagination,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesListTotal: 0,
          rolesListPagination: {},
          areRolesLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to search roles', 'error')
  }
}

export function* UPDATE_ROLE({ id, payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  const filtered = {}
  const unset = []
  _.filter(payload, function fn(n, k) {
    if (payload[k] && _.trim(payload[k]) !== '') {
      filtered[k] = n
    } else {
      unset.push(k)
    }
  })
  filtered.unset = unset

  try {
    const success = yield call(roleUpdate, id, filtered, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.ROLES_LIST,
        payload: { currentPage: 1, data: activeApp },
      })
      yield put(push(`/app/${activeApp.id}/roles`))
      window.triggerNotification('Role Saved', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Unknown Error', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to update role', 'error')
  }
}

export function* DELETE_ROLE({ payload }) {
  const { id, refresh } = payload
  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps
  const { rolesListPagination } = reduxStore.getState().roles

  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  try {
    const success = yield call(roleDelete, id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      const roleListPageNo =
        rolesListPagination !== undefined && rolesListPagination.count > 1
          ? rolesListPagination.current_page
          : 1
      yield put({
        type: actions.ROLES_LIST,
        payload: { currentPage: roleListPageNo, data: activeApp, appSettings },
      })
      window.triggerNotification('Role deleted', 'success')
      if (refresh) yield put(push(`/app/${activeApp.id}/roles`))
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      } else if (success.error.code === 'ERR_ROLE_DELETE_DENIED') {
        window.triggerNotification('Deleting Role denied', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to delete role', 'error')
  }
}

export function* ROLES_PERMISSIONS_LIST({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = payload.data || reduxStore.getState().apps.active
  const appSettings = payload.appSettings || reduxStore.getState().apps.appSettings

  try {
    const success = yield call(rolesPermissionsList, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      let totalRolePermissionsCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total) {
        totalRolePermissionsCount = success.meta.pagination.total
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsList: success.data,
          rolesPermissionsListTotal: totalRolePermissionsCount,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsList: [],
          rolesPermissionsListTotal: 0,
          areRolesLoading: false,
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch permissions', 'error')
  }
}

export function* GET_ROLE_RESTRICTIONS({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const { id } = payload
  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    let success = []
    if (!isAppModerator(activeApp) && activeApp.region !== 'legacy') {
      success = yield call(listRoleRestrictions, id, activeApp, getAppHost(appSettings))
    }

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesActiveRestrictionList: success.data,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      yield put(push(`/app/${activeApp.id}/roles`))
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch role restrictions', 'error')
  }
}

export function* ADD_ROLE_RESTRICTION({ id, payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(addRoleRestriction, id, payload, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.GET_ROLE_RESTRICTIONS,
        payload: { id },
      })
      window.triggerNotification('Feature access restricted', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to add restriction', 'error')
  }
}

export function* DELETE_ROLE_RESTRICTION({ id, payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(delRoleRestriction, id, payload, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.GET_ROLE_RESTRICTIONS,
        payload: { id },
      })
      window.triggerNotification('Feature access enabled', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message !== undefined) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to remove restriction', 'error')
  }
}

export function* ROLES_PERMISSIONS_V2_LIST({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(listRolePermissionsV2, payload.id, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2List: success.data.permissions,
          rolesPermissionsV2ListTotal: success.data.permissions.length || 0,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2List: [],
          rolesPermissionsV2ListTotal: 0,
          areRolesLoading: false,
        },
      })
      if (success.error && success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch permissions', 'error')
  }
}

export function* ROLES_PERMISSIONS_V2_VALUES_GET({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const { id } = payload
  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(getRolePermissionsV2Values, id, activeApp, getAppHost(appSettings))

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2Values: success.data.permissions,
          areRolesLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2Values: {},
          areRolesLoading: false,
        },
      })
      if (success.error && success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to fetch permission values', 'error')
  }
}

export function* ROLES_PERMISSIONS_V2_UPDATE({ id, payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(
      updateRolePermissionsV2,
      id,
      payload,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2Values: success.data.permissions,
          areRolesLoading: false,
        },
      })
      window.triggerNotification('Permissions updated successfully', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error && success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Failed to update permissions', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to update permissions', 'error')
  }
}

export function* ROLES_PERMISSIONS_V2_RESET({ id, permissionId }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areRolesLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const { appSettings } = reduxStore.getState().apps

  try {
    const success = yield call(
      resetRolePermissionsV2,
      id,
      permissionId,
      activeApp,
      getAppHost(appSettings),
    )

    if (success && success.data && success.data.permissions) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          rolesPermissionsV2Values: success.data.permissions,
          areRolesLoading: false,
        },
      })
      window.triggerNotification('Permission reset to default', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { areRolesLoading: false },
      })
      if (success.error && success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Failed to reset permission', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areRolesLoading: false },
    })
    window.triggerNotification('Failed to reset permission', 'error')
  }
}

export default function* rolesSaga() {
  yield all([
    takeEvery(actions.ROLES_LIST, ROLES_LIST),
    takeEvery(actions.ADD_ROLE, ADD_ROLE),
    takeEvery(actions.GET_ROLE, GET_ROLE),
    // takeLatest, not takeEvery: it cancels ENTER_ROLE_TEXT's debounce delay
    // when a newer keystroke arrives. With takeEvery the delay is inert.
    takeLatest(actions.ENTER_ROLE_TEXT, ENTER_ROLE_TEXT),
    takeLatest(actions.SEARCH_ROLE, SEARCH_ROLE),
    takeEvery(actions.UPDATE_ROLE, UPDATE_ROLE),
    takeEvery(actions.DELETE_ROLE, DELETE_ROLE),
    takeEvery(actions.ROLES_PERMISSIONS_LIST, ROLES_PERMISSIONS_LIST),
    takeEvery(actions.GET_ROLE_RESTRICTIONS, GET_ROLE_RESTRICTIONS),
    takeEvery(actions.ADD_ROLE_RESTRICTION, ADD_ROLE_RESTRICTION),
    takeEvery(actions.DELETE_ROLE_RESTRICTION, DELETE_ROLE_RESTRICTION),
    takeEvery(actions.ROLES_PERMISSIONS_V2_LIST, ROLES_PERMISSIONS_V2_LIST),
    takeEvery(actions.ROLES_PERMISSIONS_V2_VALUES_GET, ROLES_PERMISSIONS_V2_VALUES_GET),
    takeEvery(actions.ROLES_PERMISSIONS_V2_UPDATE, ROLES_PERMISSIONS_V2_UPDATE),
    takeEvery(actions.ROLES_PERMISSIONS_V2_RESET, ROLES_PERMISSIONS_V2_RESET),
  ])
}
